import { EntityWithIdFields } from "src/types";
import { z } from "zod";
import { SupplierOrderStatusTypes } from "./enum";
import { SupplierOrderFields, SupplierOrderFilterFields, SupplierOrderHistoryFields } from "./fields";

export const schemaSupplierOrder = z.object({
    [SupplierOrderFields.User]: z.number(),
    [SupplierOrderFields.ProviderId]: z.number().nullable(),
    [SupplierOrderFields.Provider]: z.string(),
    [SupplierOrderFields.Items]: z.string(),
    [SupplierOrderFields.DestinationClient]: z.string().nullable(),
    [SupplierOrderFields.StatusCode]: z.nativeEnum(SupplierOrderStatusTypes),
    [SupplierOrderFields.StatusDesc]: z.string(),
    [SupplierOrderFields.CreatedDate]: z.coerce.date().nullable(),
    [SupplierOrderFields.LastModifiedDate]: z.coerce.date().nullable(),
    [SupplierOrderFields.DeliveryDate]: z.coerce.date().nullable(),
    [SupplierOrderFields.Transport]: z.string().nullable(),
    [SupplierOrderFields.ResponsibleUserId]: z.number().nullable(),
    [SupplierOrderFields.ResponsibleUserName]: z.string().nullable(),
    [SupplierOrderFields.CommunicationChannelCode]: z.number().nullable(),
    [SupplierOrderFields.CommunicationChannel]: z.string().nullable(),
    [SupplierOrderFields.RequestDate]: z.coerce.date().nullable(),
    [SupplierOrderFields.DaysSinceCreation]: z.number(),
    [SupplierOrderFields.EstimatedAmount]: z.number().nullable()
})

export const schemaSupplierOrderView = schemaSupplierOrder.extend({
    [EntityWithIdFields.Id]: z.number(),
})

export type SupplierOrderViewDTO = z.infer<typeof schemaSupplierOrderView>

export const schemaSupplierOrderInsert = z.object({
    [SupplierOrderFields.ProviderId]: z.number().nullable().optional(),
    [SupplierOrderFields.Items]: z.string().min(1),
    [SupplierOrderFields.DestinationClient]: z.string().nullable(),
    [SupplierOrderFields.DeliveryDate]: z.coerce.date().nullable().optional(),
    [SupplierOrderFields.Transport]: z.string().optional().nullable(),
    [SupplierOrderFields.ResponsibleUserId]: z.number().nullable().optional(),
    [SupplierOrderFields.CommunicationChannelCode]: z.number().nullable().optional(),
    [SupplierOrderFields.RequestDate]: z.coerce.date().nullable().optional(),
    [SupplierOrderFields.EstimatedAmount]: z.string().nullable().optional(),
})

export type SupplierOrderInsertDTO = z.infer<typeof schemaSupplierOrderInsert>

export const schemaSupplierOrderUpdate = z.object({
    [SupplierOrderFields.ProviderId]: z.number().nullable().optional(),
    [SupplierOrderFields.Items]: z.string().min(1),
    [SupplierOrderFields.DestinationClient]: z.string().nullable(),
    [SupplierOrderFields.StatusCode]: z.nativeEnum(SupplierOrderStatusTypes),
    [SupplierOrderFields.DeliveryDate]: z.coerce.date().nullable().optional(),
    [SupplierOrderFields.Transport]: z.string().optional(),
    [SupplierOrderFields.ResponsibleUserId]: z.number().nullable().optional(),
    [SupplierOrderFields.CommunicationChannelCode]: z.number().nullable().optional(),
    [SupplierOrderFields.RequestDate]: z.coerce.date().nullable().optional(),
    [SupplierOrderFields.EstimatedAmount]: z.string().nullable()
}).superRefine((data, ctx) => {
    const statusWithRequiredResponsibleUser = [
        SupplierOrderStatusTypes.PENDING_DELIVERY, //2
        SupplierOrderStatusTypes.DELIVERED // 3
    ];

    if (statusWithRequiredResponsibleUser.includes(data[SupplierOrderFields.StatusCode])) {
        const responsible = data[SupplierOrderFields.ResponsibleUserId];

        // No puede ser null el responsable ni la fecha de pedido
        if (responsible === null || responsible === undefined) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "El responsable es obligatorio para el estado seleccionado",
                path: [SupplierOrderFields.ResponsibleUserId],
            });
        }

        if (data[SupplierOrderFields.RequestDate] == null) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "La fecha de pedido es obligatoria para el estado seleccionado",
                path: [SupplierOrderFields.RequestDate],
            });
        }
    }
});

export type SupplierOrderUpdateDTO = z.infer<typeof schemaSupplierOrderUpdate>

export const SupplierOrderOptionSchema = z.object({
    [SupplierOrderFields.StatusCode]: z.nativeEnum(SupplierOrderStatusTypes),
    [SupplierOrderFields.StatusDesc]: z.string(),
});

export type SupplierOrderOption = z.infer<typeof SupplierOrderOptionSchema>;


export const schemaSupplierOrderFilter = z.object({
    [SupplierOrderFilterFields.Items]: z.string().optional(),
    [SupplierOrderFilterFields.DestinationClient]: z.string().optional(),
    [SupplierOrderFilterFields.ProviderId]: z.number().optional(),
    [SupplierOrderFilterFields.StatusCodeList]: z.array(z.number()).optional(),
    [SupplierOrderFilterFields.CreatedDateStart]: z.date().optional(),
    [SupplierOrderFilterFields.CreatedDateEnd]: z.date().optional(),
    [SupplierOrderFilterFields.DeliveryDateStart]: z.date().optional(),
    [SupplierOrderFilterFields.DeliveryDateEnd]: z.date().optional(),
    [SupplierOrderFilterFields.ResponsibleUser]: z.number().optional(),
})

export type SupplierOrderFilterType = z.infer<typeof schemaSupplierOrderFilter>

export const schemaSupplierOrderHistory = z.object({
    [EntityWithIdFields.Id]: z.number(),
    [SupplierOrderHistoryFields.HistoryDate]: z.coerce.date(),
    [SupplierOrderHistoryFields.ModifiedBy]: z.string(),
    [SupplierOrderHistoryFields.Items]: z.string(),
    [SupplierOrderHistoryFields.DestinationClient]: z.string().nullable().optional(),
    [SupplierOrderHistoryFields.Transport]: z.string().nullable().optional(),
    [SupplierOrderHistoryFields.ProviderName]: z.string().nullable().optional(),
    [SupplierOrderHistoryFields.StatusDesc]: z.string(),
    [SupplierOrderHistoryFields.StatusCode]: z.nativeEnum(SupplierOrderStatusTypes),
    [SupplierOrderHistoryFields.DeliveryDate]: z.coerce.date().nullable().optional(),
    [SupplierOrderHistoryFields.RequestDate]: z.coerce.date().nullable().optional(),
    [SupplierOrderHistoryFields.ResponsibleName]: z.string().nullable().optional(),
    [SupplierOrderHistoryFields.CommChannelDesc]: z.string().nullable().optional(),
})

export type SupplierOrderHistory = z.infer<typeof schemaSupplierOrderHistory>
